import React, { useRef, useState, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { useWorldStore } from '../../stores/worldStore';

/**
 * VRPreloader — 3D loading screen visible inside the VR headset.
 * Shows a floating panel with world name + loading bar in front of the user.
 * Fades out once worldLoading becomes false.
 */
const VRPreloader: React.FC = () => {
  const isPresenting = useXR((s) => !!s.session);
  const worldLoading = useWorldStore((s) => s.worldLoading);
  const loadingMessage = useWorldStore((s) => s.loadingMessage);
  const worldName = useWorldStore((s) => s.worldName);

  const groupRef = useRef<THREE.Group>(null);
  const barRef = useRef<THREE.Mesh>(null);
  const [opacity, setOpacity] = useState(1);
  const [hidden, setHidden] = useState(false);
  const fadeStart = useRef<number | null>(null);

  // Single effect: handles both VR entry and load-complete transitions
  useEffect(() => {
    if (!isPresenting) return;

    if (worldLoading) {
      // Still loading — ensure preloader is fully visible
      fadeStart.current = null;
      setOpacity(1);
      setHidden(false);
    } else if (!fadeStart.current) {
      // Loading already done when entering VR, or just completed — start fade
      fadeStart.current = performance.now();
    }
  }, [worldLoading, isPresenting]);

  useFrame((state) => {
    if (!isPresenting || hidden) return;

    // Position panel 2m in front of camera, at eye level
    if (groupRef.current) {
      const cam = state.camera;
      const dir = new THREE.Vector3(0, 0, -1).applyQuaternion(cam.quaternion);
      groupRef.current.position.copy(cam.position).add(dir.multiplyScalar(2));
      groupRef.current.position.y = cam.position.y;
      groupRef.current.lookAt(cam.position);
    }

    // Animate loading bar
    if (barRef.current) {
      const t = (Math.sin(performance.now() * 0.003) + 1) / 2;
      barRef.current.scale.x = 0.1 + t * 0.9;
      barRef.current.position.x = (-0.5 + barRef.current.scale.x * 0.5) * 0.6;
    }

    // Fade out
    if (fadeStart.current) {
      const elapsed = performance.now() - fadeStart.current;
      const newOpacity = Math.max(0, 1 - elapsed / 1000);
      setOpacity(newOpacity);
      if (newOpacity <= 0) {
        setHidden(true);
      }
    }
  });

  if (!isPresenting || hidden) return null;

  return (
    <group ref={groupRef}>
      {/* Dark backdrop panel */}
      <mesh position={[0, 0, -0.01]}>
        <planeGeometry args={[1.2, 0.6]} />
        <meshBasicMaterial
          color="#0a0e1a"
          transparent
          opacity={opacity * 0.9}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Border glow */}
      <mesh position={[0, 0, -0.005]}>
        <planeGeometry args={[1.22, 0.62]} />
        <meshBasicMaterial
          color="#00c2b3"
          transparent
          opacity={opacity * 0.15}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* World name */}
      <Text
        position={[0, 0.12, 0]}
        fontSize={0.08}
        color="#e0e6ed"
        anchorX="center"
        anchorY="middle"
        fillOpacity={opacity}
      >
        {worldName || 'GathrXR'}
      </Text>

      {/* Loading bar background */}
      <mesh position={[0, -0.02, 0]}>
        <planeGeometry args={[0.6, 0.015]} />
        <meshBasicMaterial
          color="#1a2030"
          transparent
          opacity={opacity * 0.8}
        />
      </mesh>

      {/* Loading bar fill */}
      <mesh ref={barRef} position={[0, -0.02, 0.001]}>
        <planeGeometry args={[0.6, 0.015]} />
        <meshBasicMaterial
          color="#00c2b3"
          transparent
          opacity={opacity * 0.9}
        />
      </mesh>

      {/* Status message */}
      <Text
        position={[0, -0.08, 0]}
        fontSize={0.04}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
        fillOpacity={opacity * 0.5}
      >
        {loadingMessage}
      </Text>
    </group>
  );
};

export default VRPreloader;
